import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, MessageCircle, ImageIcon, ArrowLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { productsApi } from "@/lib/api";
import { CATEGORIES, waLink } from "@/lib/brand";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import FloatingWhatsApp from "@/components/site/FloatingWhatsApp";
import Reveal from "@/components/site/Reveal";

const ProductCard = ({ p, onOpen, delay }) => (
  <Reveal delay={delay}>
    <motion.div whileHover={{ y: -6 }} className="group bg-card rounded-2xl overflow-hidden border border-beige hover:shadow-xl transition-shadow duration-300 h-full flex flex-col" data-testid="catalog-product-card">
      <div className="relative aspect-[4/5] overflow-hidden bg-muted">
        {p.image ? (
          <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageIcon size={40} /></div>
        )}
        <span className="absolute top-3 left-3 bg-cream/90 backdrop-blur text-[10px] uppercase tracking-widest text-choco px-3 py-1 rounded-full">{p.category}</span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-serif text-2xl text-choco leading-tight">{p.name}</h3>
        {p.price ? <p className="text-gold font-semibold mt-1">{p.price}</p> : <p className="text-xs text-muted-foreground mt-1">Sob consulta</p>}
        <button onClick={() => onOpen(p)} data-testid="catalog-details-btn"
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-choco text-choco px-5 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-choco hover:text-cream transition-colors duration-300">
          <Eye size={15} /> Ver detalhes
        </button>
      </div>
    </motion.div>
  </Reveal>
);

const CategoryPage = () => {
  const { categoria } = useParams();
  const meta = CATEGORIES.find((c) => c.key === categoria);
  const [products, setProducts] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProducts(null);
    productsApi.list({ category: categoria }).then(setProducts).catch(() => setProducts([]));
  }, [categoria]);

  const title = meta ? meta.title : (categoria ? categoria.charAt(0).toUpperCase() + categoria.slice(1) : "Categoria");

  return (
    <div className="bg-cream min-h-screen">
      <Header />

      {/* Category banner */}
      <section className="relative bg-choco text-cream overflow-hidden">
        {meta && <img src={meta.image} alt={title} className="absolute inset-0 w-full h-full object-cover opacity-30" />}
        <div className="absolute inset-0 bg-gradient-to-t from-choco via-choco/70 to-choco/40" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 py-20 lg:py-28">
          <Link to="/#categorias" data-testid="catalog-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-cream/70 hover:text-gold transition-colors mb-6">
            <ArrowLeft size={15} /> Voltar às categorias
          </Link>
          <p className="text-xs uppercase tracking-luxe text-gold mb-3">Catálogo</p>
          <h1 className="font-serif text-5xl sm:text-6xl leading-none">{title}</h1>
          {meta && <p className="text-cream/70 mt-4 max-w-lg">{meta.desc}</p>}
        </div>
      </section>

      {/* Products */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-5">
          {products === null ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[0, 1, 2, 3].map((i) => <div key={i} className="rounded-2xl border border-beige bg-muted/40 aspect-[3/4] animate-pulse" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-choco/20 rounded-3xl max-w-2xl mx-auto">
              <h2 className="font-serif text-3xl text-choco mb-3">Em breve, novidades de {title}</h2>
              <p className="text-sm text-muted-foreground mb-8 max-w-md mx-auto">Ainda não há produtos cadastrados nesta categoria. Fale conosco pelo WhatsApp para conhecer as opções disponíveis e fazer sua encomenda.</p>
              <a href={waLink(`Olá! Gostaria de conhecer as opções de ${title} da Deco Sweet.`)} data-testid="catalog-empty-whatsapp"
                className="inline-flex items-center gap-2 rounded-full bg-choco text-cream px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-gold transition-colors">
                <MessageCircle size={17} /> Falar pelo WhatsApp
              </a>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-8">{products.length} produto(s) em {title}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {products.map((p, i) => <ProductCard key={p.id} p={p} delay={(i % 4) * 0.08} onOpen={setSelected} />)}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg bg-cream border-beige">
          {selected && (
            <>
              <div className="aspect-video rounded-xl overflow-hidden bg-muted mb-2">
                {selected.image ? <img src={selected.image} alt={selected.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageIcon size={40} /></div>}
              </div>
              <DialogHeader>
                <DialogTitle className="font-serif text-3xl text-choco">{selected.name}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground leading-relaxed">{selected.description || "Produto artesanal feito com carinho pela Deco Sweet. Entre em contato para mais detalhes e sabores disponíveis."}</p>
              <p className="text-gold font-semibold text-lg">{selected.price || "Sob consulta"}</p>
              <a href={waLink(`Olá! Gostaria de encomendar: ${selected.name}.`)} data-testid="catalog-dialog-whatsapp"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-choco text-cream px-6 py-3 text-sm font-semibold uppercase tracking-widest hover:bg-gold transition-colors">
                <MessageCircle size={17} /> Encomendar pelo WhatsApp
              </a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoryPage;
